import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, XCircle, Mail, MessageSquare } from "lucide-react";

export default function SlotRequests() {
  const [items, setItems] = useState(null);
  const [working, setWorking] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/therapist/me/slot-requests");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load requests.");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, kind) => {
    const payload = { reason: "" };
    if (kind === "decline") {
      const reason = window.prompt("Optional polite note to the client:", "");
      if (reason === null) return; // user pressed Cancel on prompt
      payload.reason = reason;
    }
    setWorking(`${id}-${kind}`);
    try {
      await api.post(`/therapist/me/slot-requests/${id}/${kind}`, payload);
      toast.success(kind === "accept" ? "Accepted — session booked & client notified." : "Declined — client notified.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    } finally {
      setWorking(null);
    }
  };

  if (items === null) return <div className="text-mindora-muted">loading…</div>;

  const pending = items.filter((r) => r.status === "pending");
  const handled = items.filter((r) => r.status !== "pending");

  if (items.length === 0) {
    return (
      <div className="rounded-3xl border border-mindora-line bg-mindora-panel p-10 text-center" data-testid="requests-empty">
        <p className="font-serif text-2xl text-mindora-ink">No custom requests right now.</p>
        <p className="text-mindora-muted mt-2">When a client proposes a custom time, it&apos;ll appear here with one-click Accept / Decline.</p>
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="slot-requests">
      {pending.length > 0 && (
        <section>
          <h3 className="font-serif text-2xl text-mindora-ink">Pending · {pending.length}</h3>
          <div className="mt-4 space-y-4">
            {pending.map((r) => (
              <article key={r.id} className="rounded-2xl border border-mindora-line bg-mindora-panel p-5" data-testid={`request-${r.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div>
                    <div className="font-serif text-xl text-mindora-ink leading-tight">{r.user_name || "Anonymous"}</div>
                    <div className="text-xs text-mindora-muted mt-0.5 flex items-center gap-1.5"><Mail size={11} /> {r.user_email}</div>
                    <div className="mt-3 text-xs uppercase tracking-[0.18em] text-mindora-red">Proposed</div>
                    <div className="font-serif text-2xl text-mindora-ink mt-0.5">{format(new Date(r.requested_slot), "PPP · h:mm a")}</div>
                    {r.note && (
                      <div className="mt-3 flex items-start gap-2 text-sm text-mindora-ink/85 italic">
                        <MessageSquare size={13} className="text-mindora-muted mt-0.5 shrink-0" />
                        &ldquo;{r.note}&rdquo;
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-mindora-line flex flex-wrap gap-3">
                  <Button
                    onClick={() => act(r.id, "accept")}
                    disabled={working === `${r.id}-accept`}
                    data-testid={`request-${r.id}-accept`}
                    className="btn-primary rounded-full px-5 h-9 text-sm inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 size={14} /> Accept & book
                  </Button>
                  <Button
                    onClick={() => act(r.id, "decline")}
                    disabled={working === `${r.id}-decline`}
                    variant="outline"
                    data-testid={`request-${r.id}-decline`}
                    className="rounded-full px-5 h-9 text-sm border-mindora-line text-mindora-ink hover:bg-mindora-line inline-flex items-center gap-1.5"
                  >
                    <XCircle size={14} /> Decline
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {handled.length > 0 && (
        <section>
          <h3 className="font-serif text-2xl text-mindora-ink">Already handled</h3>
          <div className="mt-4 space-y-2">
            {handled.map((r) => (
              <div key={r.id} className="rounded-xl border border-mindora-line bg-mindora-bg px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div>
                  <span className="font-serif text-base text-mindora-ink">{r.user_name || "Anonymous"}</span>
                  <span className="text-mindora-muted"> · {format(new Date(r.requested_slot), "PP · h:mm a")}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
                  r.status === "accepted" ? "bg-mindora-red/10 text-mindora-red" : "border border-mindora-line text-mindora-muted"
                }`}>{r.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
